import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ornament } from "../components/Ornament";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const RELATIONSHIPS = [
  "Mother", "Father", "Grandmother", "Grandfather",
  "Partner / Spouse", "Sibling", "Aunt", "Uncle",
  "Friend", "Other",
];

export default function StoryWeekSetup() {
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const [mode, setMode] = useState("own");
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("Mother");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendState, setResendState] = useState("idle");
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate(`/login?flow=story_week&next=${encodeURIComponent("/story-week")}`, { replace: true }); return; }
    (async () => {
      try {
        const s = await api.get("/story_week/mine");
        setExisting(s.data);
        if (s.data?.story_mode) setMode(s.data.story_mode);
        if (s.data?.story_mode === "own") {
          setName(user.name || "");
        } else if (s.data?.storyteller_name) {
          setName(s.data.storyteller_name);
        }
        if (s.data?.storyteller_relation && s.data.storyteller_relation !== "Self") {
          setRelationship(s.data.storyteller_relation);
        }
      } catch (e) { /* new session — will be created on submit */ }
    })();
    // Prefill account holder's name when the mode is "own"
  }, [user, loading, navigate]);

  useEffect(() => {
    if (mode === "own" && user) setName(user.name || "");
  }, [mode, user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError(mode === "own" ? "Please add your name." : "Please add whose story you're helping to tell."); return; }
    setBusy(true);
    try {
      const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } })();
      await api.post("/story_week/setup", {
        story_mode: mode,
        storyteller_name: name.trim(),
        relationship: mode === "own" ? "Self" : relationship,
        timezone: tz,
      });
      navigate("/story-week/day/1", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("confirm your email")) {
        setError("Please confirm your email before beginning. We've sent a link — check your inbox.");
      } else {
        setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/resend-verification", { origin_url: window.location.origin, flow: "story_week" });
      setResendState("sent");
    } catch (e) {
      setResendState("err");
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center paper" style={{ background: "#FAF7F2" }}><p className="italic text-[#8C8278]">Loading…</p></div>;
  }

  const needsVerification = !user.email_verified;

  return (
    <div className="min-h-screen" style={{ background: "#FAF7F2" }} data-testid="sw-setup-page">
      <main className="max-w-2xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <p className="chapter-roman" style={{ fontSize: "0.85rem" }}>STORY WEEK &middot; SETUP</p>
        <h1 className="text-4xl sm:text-5xl mt-4 text-[#111D33]" style={{ fontFamily: "Newsreader, serif", fontWeight: 300, lineHeight: 1.1 }}>
          Whose story are we beginning?
        </h1>
        <Ornament className="my-8 justify-start" />

        {needsVerification && (
          <div className="mb-10 p-5" style={{ background: "#F7F0E0", borderLeft: "3px solid #B8973A", borderRadius: 2 }} data-testid="sw-verify-banner">
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.25em] mb-2">CONFIRM YOUR EMAIL</p>
            <p className="text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", lineHeight: 1.65 }}>
              We've sent a confirmation link to <strong>{user.email}</strong>. Day 1 opens as soon as you click it. Check your spam folder if it's not in your inbox.
            </p>
            <button
              type="button"
              onClick={resend}
              className="mt-3 label-ui text-[11px] text-[#B8973A] hover:text-[#111D33] underline underline-offset-4"
              disabled={resendState === "sending"}
              data-testid="sw-resend-verification-btn"
            >
              {resendState === "sent" ? "Sent — check your inbox" : resendState === "sending" ? "Sending…" : "Resend the confirmation link"}
            </button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-6" data-testid="sw-setup-form" noValidate>
          <fieldset>
            <legend className="mb-3 text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", fontWeight: 500 }}>
              Who is answering the questions?
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { v: "own", l: "I'm telling my own story" },
                { v: "helping", l: "I'm helping someone tell theirs" },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className="flex items-center gap-3 px-4 py-3 hairline-cream bg-white cursor-pointer"
                  style={{ borderRadius: 4, borderColor: mode === opt.v ? "#B8973A" : undefined, minHeight: 48 }}
                  data-testid={`sw-mode-${opt.v}-label`}
                >
                  <input
                    type="radio"
                    name="mode"
                    value={opt.v}
                    checked={mode === opt.v}
                    onChange={() => setMode(opt.v)}
                    data-testid={`sw-mode-${opt.v}`}
                  />
                  <span className="text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px" }}>{opt.l}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {mode === "helping" ? (
            <>
              <div>
                <label htmlFor="sw-name" className="block mb-2 text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", fontWeight: 500 }}>
                  Whose story are you helping to tell?
                </label>
                <input
                  id="sw-name"
                  autoComplete="off"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Grandma Rose, my Dad, Uncle Frank"
                  className="w-full px-4 py-3 hairline-cream bg-white text-[#1B2A4A]"
                  style={{ borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: "16px", minHeight: 48 }}
                  data-testid="sw-name-input"
                />
              </div>
              <div>
                <label htmlFor="sw-rel" className="block mb-2 text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", fontWeight: 500 }}>
                  Their relationship to you
                </label>
                <select
                  id="sw-rel"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  className="w-full px-4 py-3 hairline-cream bg-white text-[#1B2A4A]"
                  style={{ borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: "16px", minHeight: 48 }}
                  data-testid="sw-rel-select"
                >
                  {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </>
          ) : (
            <div>
              <label htmlFor="sw-name" className="block mb-2 text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", fontWeight: 500 }}>
                Your name
              </label>
              <input
                id="sw-name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 hairline-cream bg-white text-[#1B2A4A]"
                style={{ borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: "16px", minHeight: 48 }}
                data-testid="sw-name-input"
              />
              <p className="mt-2 text-sm text-[#8C8278]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                We'll use this beside your seven answers.
              </p>
            </div>
          )}

          {error && <p className="text-red-700 text-sm" role="alert" data-testid="sw-setup-error">{error}</p>}

          <button
            type="submit"
            disabled={busy || needsVerification}
            className="btn-gold w-full sm:w-auto"
            style={{ minHeight: 52, padding: "14px 32px", fontSize: "15px" }}
            data-testid="sw-setup-submit"
          >
            {busy ? "One moment…" : needsVerification ? "Confirm your email to continue" : "Begin Day 1 →"}
          </button>
        </form>
      </main>
    </div>
  );
}
